import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { fmtEUR } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { PaymentDialog } from "@/components/dialogs/PaymentDialog";

// Confirm dialog for -> Svolta showing credit impact, then (for a chargeable standalone
// session with an unpaid residual) a lightweight payment reminder. The reminder is UX only:
// the service stays Svolta regardless, and no payment is created without explicit save.
export const CompleteDialog = ({ open, onOpenChange, session, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [makePkg, setMakePkg] = useState(false);
  const [pkg, setPkg] = useState({ total_credits: 10, total_price: 0 });
  const [step, setStep] = useState("confirm");
  const [residual, setResidual] = useState(0);
  const [collect, setCollect] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const linked = session?.package_linked && !session?.is_free;
  const primaryDog = session?.dog_ids?.[0];

  useEffect(() => {
    if (!open || !session) return;
    setStep("confirm");
    setResidual(0);
    setMakePkg(false);
    setPkg({ total_credits: 10, total_price: 0 });
    if (linked && primaryDog) {
      api.post("/sessions/preview-credits", null, {
        params: { dog_id: primaryDog, duration_minutes: session.duration_minutes, credits: session.credits || 0 },
      }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    } else {
      setPreview(null);
    }
  }, [open, session, linked, primaryDog]);

  const confirm = async () => {
    setSaving(true);
    try {
      const body = { status: "Svolta" };
      if (preview?.overflow > 0 && makePkg) {
        body.overflow_package = { total_credits: Number(pkg.total_credits), total_price: Number(pkg.total_price) };
      }
      await api.patch(`/sessions/${session.id}/status`, body);
      toast.success("Prestazione segnata come svolta");
      onDone?.();
      // Evaluate the canonical collectible residual AFTER completion (credit/package ops done).
      let sum = null;
      try { const r = await api.get(`/sessions/${session.id}/payment-summary`); sum = r.data; } catch (e) { sum = null; }
      if (sum && sum.is_collectible && sum.residual_amount > 0) {
        setResidual(sum.residual_amount);
        setCollect({ owner_id: session.owner_id, dog_id: primaryDog || "", session_id: session.id, amount: sum.residual_amount });
        setStep("collect");
      } else {
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  const registra = () => { onOpenChange(false); setStep("confirm"); setPayOpen(true); };
  const piuTardi = () => { onOpenChange(false); setStep("confirm"); };

  const ap = preview?.active_package;
  const blocked = preview?.overflow > 0 && !makePkg;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          {step === "collect" ? (
            <>
              <DialogHeader><DialogTitle className="font-head">Prestazione svolta</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="rounded-xl border border-[#E7C9A8] bg-[#FBF2E7] p-4">
                  <div className="text-sm text-muted-foreground">Resta da incassare</div>
                  <div className="text-2xl font-extrabold tabular" data-testid="complete-residual">{fmtEUR(residual)}</div>
                </div>
                <p className="text-sm">Vuoi registrare il pagamento adesso?</p>
              </div>
              <DialogFooter>
                <Button variant="outline" data-testid="complete-later" onClick={piuTardi}>Più tardi</Button>
                <Button data-testid="complete-register-payment" onClick={registra}>Registra pagamento</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle className="font-head">Segna come svolta</DialogTitle></DialogHeader>
              {session && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-border p-3 text-sm">
                    <div className="font-semibold">{(session.dog_names || []).join(", ")}</div>
                    <div className="text-muted-foreground">{session.service_type} · {fmtDuration(session.duration_minutes) || "—"}</div>
                  </div>

                  {!linked && !(session?.engine === "v2" && (session?.credits || 0) > 0) && <p className="text-sm text-muted-foreground">Questa prestazione non scala crediti.</p>}
                  {session?.engine === "v2" && (session?.credits || 0) > 0 && (
                    <p className="text-sm text-muted-foreground" data-testid="complete-v2-credits">Verranno scalati <b>{session.credits}</b> {session.credits === 1 ? "credito" : "crediti"} secondo la copertura impostata. Se i crediti non bastano, il completamento verrà bloccato.</p>
                  )}

                  {linked && (
                    <div className="rounded-xl border border-[#B7D3C4] bg-[#E6EFEA] p-3 space-y-2">
                      {ap ? (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>Pacchetto: {ap.used_credits}/{ap.total_credits}</span>
                            <span className="font-mono">{ap.remaining_credits} → {Math.max(0, ap.remaining_credits - (preview?.credits_needed || 0))} residui</span>
                          </div>
                          <Progress value={(ap.used_credits / ap.total_credits) * 100} className="h-2" />
                        </>
                      ) : <p className="text-sm text-[#8D6E63]">Nessun pacchetto attivo.</p>}
                      {preview && <p className="text-sm">Crediti da consumare: <b>{preview.credits_needed}</b></p>}
                      {preview?.overflow > 0 && (
                        <div className="space-y-2">
                          <div className="text-sm text-[#C62828]">
                            <p className="flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> {preview.overflow === 1 ? "Credito insufficiente: manca 1 credito." : `Crediti insufficienti: mancano ${preview.overflow} crediti.`}</p>
                            <p className="mt-1 text-xs">Riduci i crediti richiesti oppure crea/seleziona un altro pacchetto.</p>
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Crea nuovo pacchetto (Da saldare)</Label>
                            <Switch checked={makePkg} onCheckedChange={setMakePkg} data-testid="complete-make-pkg" />
                          </div>
                          {makePkg && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><Label className="text-xs">Crediti</Label><Input type="number" value={pkg.total_credits} onChange={(e) => setPkg((p) => ({ ...p, total_credits: e.target.value }))} /></div>
                              <div><Label className="text-xs">Prezzo €</Label><Input type="number" value={pkg.total_price} onChange={(e) => setPkg((p) => ({ ...p, total_price: e.target.value }))} /></div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
                <Button data-testid="complete-confirm" onClick={confirm} disabled={saving || blocked}>{saving ? "…" : "Conferma"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        presetOwnerId={collect?.owner_id}
        presetDogId={collect?.dog_id}
        presetSessionId={collect?.session_id}
        presetAmount={collect?.amount}
        onSaved={() => { setPayOpen(false); onDone?.(); }}
      />
    </>
  );
};
